"use client";

import { c as _c } from "react/compiler-runtime";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownSettingsButton } from "../ui/dropdown";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { FiChevronDown, FiChevronRight, FiClock, FiPlus } from "react-icons/fi";
import { useParams } from "next/navigation";
import DeleteConfirmationModal from "@/components/delete-confirmation-modal";
import PatientCreditTransferModal from "@/components/patient-credit-transfer-modal";
import { CreditReasonField, useCreditReasonField } from "@/components/patient/credit-reason-field";
import { ProgressBarLink } from "@/components/progress-bar";
import TiptapEditor from "@/components/tiptap/editor";
import { Button, Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Label, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableHeaderRow, TableRow } from "@/components/ui";
import { DatePickerWithYearDropdown } from "@/components/ui/date-picker";
import DollarInput from "@/components/ui/dollar-input";
import { useUser } from "@/contexts/user-provider";
import { formatCurrency, formatDate, formatStaffName } from "@/lib/format-data";
import { parseError } from "@/lib/parse-error";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";
import type { TransferPatientCreditRequest } from "@/types/billing/patient-credit-transfer";
import type { PatientCredit, PatientCreditCreate, PatientCreditTransaction, PatientCreditTransactionType, PatientCreditUpdate } from "@/types/patient-credit";
import { transferPatientCredit } from "@/api/provider/billing/patient-credit-transfer";
import { createPatientCredit, deletePatientCredit, getPatientCreditTransactions, getPatientCredits, updatePatientCredit } from "@/api/provider/user/patient/patient-credit";
import { patientCreditReasonQueryKeys } from "@/query/settings/patient-credit-reason";
const stickyActionColumnBaseClassName = cx("sticky right-0 w-24 min-w-24 !overflow-visible border-l border-border pl-8 pr-2 text-right", "before:pointer-events-none before:absolute before:inset-y-0 before:-left-10 before:w-10 before:content-['']", "before:bg-gradient-to-r before:from-transparent");
interface PatientCreditsDialogProps {
  patientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export function PatientCreditsDialog({
  patientId,
  open,
  onOpenChange
}: PatientCreditsDialogProps) {
  const params = useParams<{
    tenant?: string;
  }>();
  const tenant = params?.tenant;
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCredit, setEditingCredit] = useState<PatientCredit | null>(null);
  const [creditToDelete, setCreditToDelete] = useState<PatientCredit | null>(null);
  const [transferCredit, setTransferCredit] = useState<PatientCredit | null>(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [expandedCreditId, setExpandedCreditId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const {
    toast
  } = useToast();
  const {
    currentUser
  } = useUser();
  const canTransferCredits = hasPermission(currentUser, PERMISSIONS.PATIENT_CREDITS_TRANSFER);
  const canAdjustCredits = hasPermission(currentUser, PERMISSIONS.BILLING_BALANCES_ADJUST);
  const handleTransferSubmit = async (req: TransferPatientCreditRequest) => {
    if (!transferCredit) return;
    setIsTransferring(true);
    try {
      await transferPatientCredit(transferCredit.id, req);
      // Invalidate every cache that exposes the patient's credit totals:
      // the paged list, the live available-credit total (read by checkout),
      // and both sides of the transfer.
      await Promise.all([queryClient.invalidateQueries({
        queryKey: ["patient-credits", patientId]
      }), queryClient.invalidateQueries({
        queryKey: ["patient-credits", req.toPatientId]
      }), queryClient.invalidateQueries({
        queryKey: ["available-credit", patientId]
      }), queryClient.invalidateQueries({
        queryKey: ["available-credit", req.toPatientId]
      })]);
      toast({
        title: "Credit transferred",
        description: `Moved ${formatCurrency(req.amount)} to the recipient patient.`
      });
      setTransferCredit(null);
    } catch (error) {
      toast({
        title: "Failed to transfer credit",
        description: await parseError(error),
        variant: "error"
      });
    } finally {
      setIsTransferring(false);
    }
  };
  const {
    data: creditsPage,
    isLoading
  } = useQuery({
    queryKey: ["patient-credits", patientId],
    queryFn: () => getPatientCredits(patientId, {
      perPage: 100,
      includeUsed: true
    }),
    enabled: open
  });
  const createMutation = useMutation({
    mutationFn: (data: PatientCreditCreate) => createPatientCredit(patientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patient-credits", patientId]
      });
      queryClient.invalidateQueries({
        queryKey: ["available-credit", patientId]
      });
      setShowCreateForm(false);
      toast({
        title: "Success",
        description: "Credit added successfully",
        variant: "success"
      });
    },
    onError: async error_0 => {
      // A refusal may mean the reason list changed under this form; reload it.
      void queryClient.refetchQueries({
        queryKey: patientCreditReasonQueryKeys.all
      });
      toast({
        title: "Couldn't add credit",
        description: await parseError(error_0, "Failed to add credit"),
        variant: "error"
      });
    }
  });
  const updateMutation = useMutation({
    mutationFn: ({
      creditId,
      data: data_0
    }: {
      creditId: string;
      data: PatientCreditUpdate;
    }) => updatePatientCredit(patientId, creditId, data_0),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patient-credits", patientId]
      });
      queryClient.invalidateQueries({
        queryKey: ["available-credit", patientId]
      });
      setEditingCredit(null);
      toast({
        title: "Success",
        description: "Credit updated successfully",
        variant: "success"
      });
    },
    onError: async error_1 => {
      void queryClient.refetchQueries({
        queryKey: patientCreditReasonQueryKeys.all
      });
      toast({
        title: "Couldn't update credit",
        description: await parseError(error_1, "Failed to update credit"),
        variant: "error"
      });
    }
  });
  const deleteMutation = useMutation({
    mutationFn: (creditId_0: string) => deletePatientCredit(patientId, creditId_0),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patient-credits", patientId]
      });
      queryClient.invalidateQueries({
        queryKey: ["available-credit", patientId]
      });
      setCreditToDelete(null);
      toast({
        title: "Success",
        description: "Credit deleted successfully",
        variant: "success"
      });
    },
    onError: async (error_2: any) => {
      const message = await parseError(error_2, "Failed to delete credit");
      toast({
        title: "Error",
        description: message,
        variant: "error"
      });
    }
  });
  const credits = creditsPage?.data || [];
  const totalAvailable = credits.reduce((sum: number, credit: PatientCredit) => sum + credit.amountRemaining, 0);
  const handleCancelForm = () => {
    setShowCreateForm(false);
    setEditingCredit(null);
  };
  return <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Patient Credits</DialogTitle>
            <DialogDescription>
              Manage patient credit balance. Total available:{" "}
              {formatCurrency(totalAvailable)}
            </DialogDescription>
          </DialogHeader>

          <DialogBody>
            {showCreateForm && <CreditForm onSubmit={data_1 => createMutation.mutate(data_1)} onCancel={handleCancelForm} isLoading={createMutation.isPending} />}

            {editingCredit && <CreditForm credit={editingCredit} onSubmit={data_2 => updateMutation.mutate({
            creditId: editingCredit.id,
            data: data_2
          })} onCancel={handleCancelForm} isLoading={updateMutation.isPending} />}

            {!showCreateForm && !editingCredit && <div className="space-y-4">
                {canAdjustCredits && <div className="flex justify-end">
                    <Button variant="secondary" onClick={() => setShowCreateForm(true)} icon={<FiPlus />}>
                      Add Credit
                    </Button>
                  </div>}

                {isLoading ? <div className="py-8 text-center text-body text-muted-foreground">
                    Loading credits...
                  </div> : credits.length === 0 ? <div className="py-8 text-center text-body text-muted-foreground">
                    No credits found for this patient
                  </div> : <div className="overflow-hidden rounded-lg border border-border">
                    <Table>
                      <TableHead className="bg-background-secondary">
                        <TableHeaderRow>
                          <TableHeaderCell className="w-1/12 font-semibold">
                            Amount
                          </TableHeaderCell>
                          <TableHeaderCell className="w-1/8 font-semibold">
                            Remaining
                          </TableHeaderCell>
                          <TableHeaderCell className="w-1/3 font-semibold">
                            Reason
                          </TableHeaderCell>
                          <TableHeaderCell className="w-1/8 font-semibold">
                            Status
                          </TableHeaderCell>
                          <TableHeaderCell className="w-1/6 font-semibold">
                            Dates
                          </TableHeaderCell>
                          <TableHeaderCell className="w-1/8 font-semibold">
                            Created By
                          </TableHeaderCell>
                          <TableHeaderCell aria-label="Credit actions" className={cx(stickyActionColumnBaseClassName, "z-20 bg-background-secondary font-semibold before:via-background-secondary/80 before:to-background-secondary", "border-b border-border")}>
                            <span className="sr-only">Actions</span>
                          </TableHeaderCell>
                        </TableHeaderRow>
                      </TableHead>
                      <TableBody className="divide-y divide-border">
                        {credits.map((credit_0: PatientCredit) => {
                    const isExpired = credit_0.expiryDate && new Date(credit_0.expiryDate) < new Date();
                    // Prefer the server-computed status (distinguishes
                    // TRANSFERRED from USED via the balance ledger) but
                    // fall back to the amount-remaining heuristic when
                    // the server didn't include it (e.g. cached older
                    // responses).
                    const status = credit_0.status ?? (credit_0.amountRemaining === 0 ? "USED" : "ACTIVE");
                    const isUsed = status === "USED";
                    const isTransferred = status === "TRANSFERRED";
                    const isArchived = status === "ARCHIVED";
                    const amountUsed = credit_0.amount - credit_0.amountRemaining;
                    const isExpanded = expandedCreditId === credit_0.id;
                    return <Fragment key={credit_0.id}>
                              <TableRow aria-expanded={isExpanded} onClick={() => setExpandedCreditId(isExpanded ? null : credit_0.id)} className="group cursor-pointer hover:bg-background-secondary">
                                <TableCell>
                                  <div className="flex items-center gap-1.5 font-semibold text-foreground">
                                    {isExpanded ? <FiChevronDown className="h-4 w-4 shrink-0 text-foreground-muted" /> : <FiChevronRight className="h-4 w-4 shrink-0 text-foreground-muted" />}
                                    <span>{formatCurrency(credit_0.amount)}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="font-semibold text-foreground">
                                    {formatCurrency(credit_0.amountRemaining)}
                                  </div>
                                  {amountUsed > 0 && (() => {
                            const destinations = credit_0.transfersOut ?? [];
                            // Single transfer → one inline caption with
                            // the link. Multiple partial transfers →
                            // stack each destination on its own line so
                            // staff see every recipient.
                            if (destinations.length === 1) {
                              const dest = destinations[0];
                              return <div className="mt-0.5 text-caption text-muted-foreground">
                                            {formatCurrency(amountUsed)}{" "}
                                            transferred to{" "}
                                            <ProgressBarLink href={`/patient/${dest.toPatientId}?tab=billing`} className="text-primary hover:underline">
                                              {dest.toPatientName ?? "another patient"}
                                            </ProgressBarLink>
                                          </div>;
                            }
                            if (destinations.length > 1) {
                              return <div className="mt-0.5 space-y-0.5 text-caption text-muted-foreground">
                                            <div>
                                              {formatCurrency(amountUsed)}{" "}
                                              transferred:
                                            </div>
                                            {destinations.map(dest_0 => <div key={dest_0.id} className="pl-2">
                                                {formatCurrency(dest_0.amount)} to{" "}
                                                <ProgressBarLink href={`/patient/${dest_0.toPatientId}?tab=billing`} className="text-primary hover:underline">
                                                  {dest_0.toPatientName ?? "another patient"}
                                                </ProgressBarLink>
                                              </div>)}
                                          </div>;
                            }
                            return <div className="mt-0.5 text-caption text-muted-foreground">
                                          {formatCurrency(amountUsed)}{" "}
                                          {isTransferred ? <span>{"transferred"}</span> : <span>{"used"}</span>}
                                        </div>;
                          })()}
                                </TableCell>
                                <TableCell>
                                  <div className="max-w-md space-y-1 text-body text-foreground-secondary">
                                    {credit_0.reason && <div className="text-body-strong text-foreground">
                                        {credit_0.reason}
                                      </div>}
                                    {credit_0.comment ? <div className="whitespace-pre-wrap break-words">
                                        {credit_0.comment}
                                      </div> : !credit_0.reason && <span className="text-foreground-muted">
                                          No comment
                                        </span>}
                                    {credit_0.sourceRefund && (tenant ? <a href={`/${tenant}/billing/payments?id=${credit_0.sourceRefund.paymentId}`} className="text-caption text-primary hover:underline">
                                          View source payment →
                                        </a> : <div className="text-caption text-foreground-muted">
                                          Issued by refund on payment{" "}
                                          <span className="font-mono">
                                            {credit_0.sourceRefund.paymentId}
                                          </span>
                                        </div>)}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {isExpired ? <span className="inline-flex items-center gap-1.5 rounded-full bg-status-error-bg px-2.5 py-1 text-caption-strong text-status-error-text ring-1 ring-inset ring-status-error-text/20">
                                      <FiClock className="h-3.5 w-3.5" />{" "}
                                      Expired
                                    </span> : isTransferred ? <span className="inline-flex items-center rounded-full bg-status-info-bg px-2.5 py-1 text-caption-strong text-status-info-text ring-1 ring-inset ring-status-info-text/20">
                                      Transferred
                                    </span> : isArchived ? <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 text-caption-strong text-foreground-secondary ring-1 ring-inset ring-border/20">
                                      Archived
                                    </span> : isUsed ? <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 text-caption-strong text-foreground-secondary ring-1 ring-inset ring-border/20">
                                      Used
                                    </span> : <span className="inline-flex items-center rounded-full bg-status-success-bg px-2.5 py-1 text-caption-strong text-status-success-text ring-1 ring-inset ring-status-success-text/20">
                                      Active
                                    </span>}
                                </TableCell>
                                <TableCell>
                                  <div className="space-y-0.5 text-caption">
                                    <div className="text-foreground-secondary">
                                      <span className="font-medium">
                                        Added:
                                      </span>{" "}
                                      {formatDate(credit_0.createdDate)}
                                    </div>
                                    {credit_0.expiryDate ? <div className="text-foreground-secondary">
                                        <span className="font-medium">
                                          Expires:
                                        </span>{" "}
                                        {formatDate(credit_0.expiryDate)}
                                      </div> : <div className="text-foreground-muted">
                                        Never expires
                                      </div>}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="text-body">
                                    {credit_0.creator ? <span className="font-medium text-foreground">
                                        {formatStaffName(credit_0.creator)}
                                      </span> : <span className="italic text-foreground-muted">
                                        System
                                      </span>}
                                  </div>
                                </TableCell>
                                <TableCell onClick={e => e.stopPropagation()} className={cx(stickyActionColumnBaseClassName, "z-10 bg-background before:via-background/85 before:to-background", "group-hover:bg-background-secondary group-hover:before:via-background-secondary/85 group-hover:before:to-background-secondary", "border-t border-border [.group:first-child_&]:border-t-0")}>
                                  {canAdjustCredits || canTransferCredits && credit_0.amountRemaining > 0 ? <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <DropdownSettingsButton />
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        {/* Refund-issued credits are locked: the
                                          Refund is SUCCEEDED and editing/deleting
                                          the credit would leave the patient short
                                          the refund value without a reversal. */}
                                        {canAdjustCredits && <DropdownMenuItem onClick={() => setEditingCredit(credit_0)} disabled={!!credit_0.sourceRefund}>
                                            Edit
                                          </DropdownMenuItem>}
                                        {canTransferCredits && credit_0.amountRemaining > 0 && <DropdownMenuItem onClick={() => setTransferCredit(credit_0)}>
                                              Transfer to another patient
                                            </DropdownMenuItem>}
                                        {canAdjustCredits && <DropdownMenuItem onClick={() => setCreditToDelete(credit_0)} disabled={credit_0.amountRemaining !== credit_0.amount || !!credit_0.sourceRefund}>
                                            Delete
                                          </DropdownMenuItem>}
                                      </DropdownMenuContent>
                                    </DropdownMenu> : <span className="sr-only">
                                      No actions available
                                    </span>}
                                </TableCell>
                              </TableRow>
                              {isExpanded && <TableRow className="bg-background-secondary/50">
                                  <TableCell colSpan={7} className="px-6 py-4">
                                    <CreditActivity patientId={patientId} creditId={credit_0.id} />
                                  </TableCell>
                                </TableRow>}
                            </Fragment>;
                  })}
                      </TableBody>
                    </Table>
                  </div>}
              </div>}
          </DialogBody>

          {!showCreateForm && !editingCredit && <DialogFooter>
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>}
        </DialogContent>
      </Dialog>

      <DeleteConfirmationModal isOpen={!!creditToDelete} setIsOpen={open_0 => !open_0 && setCreditToDelete(null)} onConfirm={() => {
      if (creditToDelete) {
        deleteMutation.mutate(creditToDelete.id);
      }
    }} title="Delete Credit" subtitle={`Are you sure you want to delete this credit of ${creditToDelete ? formatCurrency(creditToDelete.amount) : ""}? This action cannot be undone.`} confirmText="Delete" isLoading={deleteMutation.isPending} />

      <PatientCreditTransferModal isOpen={transferCredit !== null} setIsOpen={open_1 => !open_1 && setTransferCredit(null)} credit={transferCredit ? {
      id: transferCredit.id,
      amount: transferCredit.amount,
      amountRemaining: transferCredit.amountRemaining
    } : null} fromPatientId={patientId} isLoading={isTransferring} onSubmit={handleTransferSubmit} />
    </>;
}
const TRANSACTION_LABELS: Record<PatientCreditTransactionType, string> = {
  REDEMPTION: "Used",
  REDEMPTION_REVERSAL: "Returned (payment refunded)",
  REFUND: "Returned (refund)",
  ADJUSTMENT_INCREASE: "Increased by staff",
  ADJUSTMENT_DECREASE: "Decreased by staff",
  EXPIRY: "Expired",
  PURCHASE_REFUND: "Removed (purchase refunded)",
  VOID: "Voided",
  TRANSFER_OUT: "Transferred out",
  TRANSFER_IN: "Transferred in"
};
const INCREASING_TYPES: ReadonlySet<PatientCreditTransactionType> = new Set<PatientCreditTransactionType>(["REDEMPTION_REVERSAL", "REFUND", "ADJUSTMENT_INCREASE", "TRANSFER_IN"]);
function CreditActivity(t0) {
  const $ = _c(10);
  const {
    patientId,
    creditId
  } = t0;
  let t1;
  if ($[0] !== creditId || $[1] !== patientId) {
    t1 = {
      queryKey: ["patient-credit-transactions", patientId, creditId],
      queryFn: () => getPatientCreditTransactions(patientId, creditId)
    };
    $[0] = creditId;
    $[1] = patientId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: transactions,
    isLoading
  } = useQuery(t1);
  if (isLoading) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="text-caption text-muted-foreground">Loading activity...</div>;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (!transactions?.length) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="text-caption text-muted-foreground">This credit hasn't been used yet.</div>;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="text-caption-strong text-foreground-secondary">Credit activity</div>;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== transactions) {
    t3 = transactions.map(_temp);
    $[6] = transactions;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <div className="space-y-3">{t2}<ul className="divide-y divide-border rounded-md border border-border bg-background">{t3}</ul></div>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function _temp(txn) {
  return <CreditActivityRow key={txn.id} txn={txn} />;
}
function CreditActivityRow(t0) {
  const $ = _c(42);
  const {
    txn
  } = t0;
  const isIncrease = INCREASING_TYPES.has(txn.type);
  const t1 = TRANSACTION_LABELS[txn.type];
  let t2;
  if ($[0] !== t1) {
    t2 = <span className="font-medium">{t1}</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== txn.createdDate) {
    t3 = formatDate(txn.createdDate);
    $[2] = txn.createdDate;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = ` · ${t3}`;
  let t5;
  if ($[4] !== t4) {
    t5 = <span>{t4}</span>;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== txn.actorName) {
    t6 = txn.actorName && <span>{` · ${txn.actorName}`}</span>;
    $[6] = txn.actorName;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t5 || $[9] !== t6) {
    t7 = <span className="text-foreground-secondary">{t5}{t6}</span>;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t2 || $[12] !== t7) {
    t8 = <div className="text-body text-foreground">{t2}{t7}</div>;
    $[11] = t2;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== txn.charges) {
    t9 = txn.charges.map(_temp3);
    $[14] = txn.charges;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== txn.charges.length || $[17] !== txn.paymentId || $[18] !== txn.type) {
    t10 = txn.type === "REDEMPTION" && txn.charges.length === 0 && <div className="text-caption text-foreground-muted">{txn.paymentId ? "No purchase linked to this payment" : "Balance adjustment \u2014 not tied to a purchase"}</div>;
    $[16] = txn.charges.length;
    $[17] = txn.paymentId;
    $[18] = txn.type;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== txn.note) {
    t11 = txn.note && <div className="text-caption text-foreground-secondary">{txn.note}</div>;
    $[20] = txn.note;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== txn.paymentId) {
    t12 = txn.paymentId && <ProgressBarLink href={`/billing/payments?id=${txn.paymentId}`} className="text-caption text-primary hover:underline">View payment →</ProgressBarLink>;
    $[22] = txn.paymentId;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t12 || $[27] !== t8 || $[28] !== t9) {
    t13 = <div className="min-w-0 space-y-1">{t8}{t9}{t10}{t11}{t12}</div>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
    $[27] = t8;
    $[28] = t9;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const t14 = isIncrease ? "text-status-success-text" : "text-foreground";
  let t15;
  if ($[30] !== t14) {
    t15 = cx("shrink-0 text-body-strong", t14);
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== txn.amount) {
    t16 = formatCurrency(txn.amount);
    $[32] = txn.amount;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const t17 = `${isIncrease ? "+" : "\u2212"}${t16}`;
  let t18;
  if ($[34] !== t17) {
    t18 = <span>{t17}</span>;
    $[34] = t17;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  let t19;
  if ($[36] !== t15 || $[37] !== t18) {
    t19 = <div className={t15}>{t18}</div>;
    $[36] = t15;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t13 || $[40] !== t19) {
    t20 = <li className="flex items-start justify-between gap-4 px-4 py-3">{t13}{t19}</li>;
    $[39] = t13;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  return t20;
}
function _temp3(charge) {
  return <div key={charge.id} className="text-caption text-foreground-secondary"><span>{charge.items.length > 0 ? charge.items.map(_temp2).join(", ") : charge.description ?? "Charge"}</span>{" "}<span className="text-foreground-muted">({formatDate(charge.createdDate)})</span>{" "}<ProgressBarLink href={`/billing/charges?id=${charge.id}`} className="text-primary hover:underline">View charge</ProgressBarLink></div>;
}
function _temp2(item) {
  return item.quantity === 1 ? item.name : `${item.name} × ${item.quantity}`;
}
interface CreditFormProps {
  credit?: PatientCredit;
  onSubmit: (data: Omit<PatientCreditCreate, "patientId">) => void;
  onCancel: () => void;
  isLoading: boolean;
}
function CreditForm(t0) {
  const $ = _c(64);
  const {
    credit,
    onSubmit,
    onCancel,
    isLoading
  } = t0;
  const {
    toast
  } = useToast();
  const [amount, setAmount] = useState(credit ? credit.amount / 100 : 0);
  const [amountRemaining, setAmountRemaining] = useState(credit ? credit.amountRemaining / 100 : 0);
  const [comment, setComment] = useState(credit?.comment || "");
  const [pickedReasonId, setReasonId] = useState(credit?.patientCreditReasonId ?? "");
  const [reasonError, setReasonError] = useState(null);
  let t1;
  if ($[0] !== credit) {
    t1 = credit?.patientCreditReasonId && credit.reason ? {
      id: credit.patientCreditReasonId,
      name: credit.reason
    } : null;
    $[0] = credit;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const reasonField = useCreditReasonField(t1);
  const isManual = !credit || (!credit.sourceType || credit.sourceType === "MANUAL") && !(credit.reason && !credit.patientCreditReasonId);
  let t2;
  if ($[2] !== pickedReasonId || $[3] !== reasonField) {
    t2 = reasonField.offered(pickedReasonId);
    $[2] = pickedReasonId;
    $[3] = reasonField;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const reasonId = t2;
  const reasonChanged = reasonId !== (credit?.patientCreditReasonId ?? "");
  const requiresReason = isManual && reasonField.hasConfiguredReasons && (!credit || reasonChanged);
  let t3;
  if ($[5] !== credit) {
    t3 = credit?.expiryDate ? new Date(credit.expiryDate) : undefined;
    $[5] = credit;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const [expiryDate, setExpiryDate] = useState(t3);
  let t4;
  if ($[7] !== amount || $[8] !== amountRemaining || $[9] !== comment || $[10] !== credit || $[11] !== expiryDate || $[12] !== isManual || $[13] !== onSubmit || $[14] !== reasonChanged || $[15] !== reasonId || $[16] !== requiresReason || $[17] !== toast) {
    t4 = e => {
      e.preventDefault();
      const amountInCents = Math.round(amount * 100);
      const amountRemainingInCents = Math.round(amountRemaining * 100);
      if (amountRemainingInCents > amountInCents) {
        toast({
          title: "Validation Error",
          description: "Remaining amount cannot exceed total amount",
          variant: "error"
        });
        return;
      }
      if (requiresReason && !reasonId) {
        setReasonError("Select a reason for this credit.");
        return;
      }
      const submitData = {
        amount: amountInCents,
        comment: comment.trim() || null,
        expiryDate: expiryDate ? expiryDate.toISOString() : null
      };
      if (isManual && (!credit || reasonChanged)) {
        submitData.patientCreditReasonId = reasonId || null;
      }
      if (credit) {
        submitData.amountRemaining = amountRemainingInCents;
      }
      onSubmit(submitData);
    };
    $[7] = amount;
    $[8] = amountRemaining;
    $[9] = comment;
    $[10] = credit;
    $[11] = expiryDate;
    $[12] = isManual;
    $[13] = onSubmit;
    $[14] = reasonChanged;
    $[15] = reasonId;
    $[16] = requiresReason;
    $[17] = toast;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const handleSubmit = t4;
  let t5;
  if ($[19] !== credit) {
    t5 = newAmount => {
      setAmount(newAmount);
      if (!credit) {
        setAmountRemaining(newAmount);
      }
    };
    $[19] = credit;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const handleAmountChange = t5;
  let t6;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Label htmlFor="amount">Amount</Label>;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== amount || $[23] !== handleAmountChange) {
    t7 = <div className="space-y-2">{t6}<DollarInput id="amount" value={amount} onValueChange={handleAmountChange} min={0} placeholder="0.00" required={true} /></div>;
    $[22] = amount;
    $[23] = handleAmountChange;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== amount || $[26] !== amountRemaining || $[27] !== credit) {
    t8 = credit && <div className="space-y-2"><Label htmlFor="amountRemaining">Remaining Amount</Label><DollarInput id="amountRemaining" value={amountRemaining} onValueChange={setAmountRemaining} min={0} max={amount} placeholder="0.00" />{amountRemaining > amount && <p className="text-caption text-status-error-text">Remaining amount cannot exceed total amount</p>}</div>;
    $[25] = amount;
    $[26] = amountRemaining;
    $[27] = credit;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== isManual || $[30] !== reasonError || $[31] !== reasonField || $[32] !== reasonId) {
    t9 = isManual && <CreditReasonField value={reasonId} onChange={value => {
      setReasonId(value);
      setReasonError(null);
    }} error={reasonError} field={reasonField} />;
    $[29] = isManual;
    $[30] = reasonError;
    $[31] = reasonField;
    $[32] = reasonId;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Label htmlFor="comment">Comment (optional)</Label>;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  let t12;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = [];
    t12 = text => setComment(text);
    $[35] = t11;
    $[36] = t12;
  } else {
    t11 = $[35];
    t12 = $[36];
  }
  let t13;
  if ($[37] !== comment) {
    t13 = <div className="space-y-2">{t10}<TiptapEditor content={comment} placeholder="Add details about this credit..." suggestions={t11} onUpdate={t12} textArea={true} editorClassName="min-h-[100px]" parentClassName="w-full" /></div>;
    $[37] = comment;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Label htmlFor="expiry-date">Expiry Date (optional)</Label>;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = new Date();
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== expiryDate) {
    t16 = <DatePickerWithYearDropdown value={expiryDate} onChange={setExpiryDate} placeholder="Select expiry date" minDate={t15} className="w-full" />;
    $[41] = expiryDate;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <p className="text-caption text-muted-foreground">Leave empty for credits that never expire</p>;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t16) {
    t18 = <div className="space-y-2">{t14}{t16}{t17}</div>;
    $[44] = t16;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== isLoading || $[47] !== onCancel) {
    t19 = <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>Cancel</Button>;
    $[46] = isLoading;
    $[47] = onCancel;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  const t20 = isLoading || isManual && reasonField.isUnresolved;
  const t21 = credit ? "Update Credit" : "Add Credit";
  let t22;
  if ($[49] !== isLoading || $[50] !== t20 || $[51] !== t21) {
    t22 = <Button type="submit" disabled={t20} isLoading={isLoading}>{t21}</Button>;
    $[49] = isLoading;
    $[50] = t20;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t19 || $[54] !== t22) {
    t23 = <DialogFooter>{t19}{t22}</DialogFooter>;
    $[53] = t19;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== handleSubmit || $[57] !== t13 || $[58] !== t18 || $[59] !== t23 || $[60] !== t7 || $[61] !== t8 || $[62] !== t9) {
    t24 = <form onSubmit={handleSubmit} className="space-y-4">{t7}{t8}{t9}{t13}{t18}{t23}</form>;
    $[56] = handleSubmit;
    $[57] = t13;
    $[58] = t18;
    $[59] = t23;
    $[60] = t7;
    $[61] = t8;
    $[62] = t9;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  return t24;
}